import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import get from 'lodash/get';
import { createSelector } from '@reduxjs/toolkit';

import { useAuthenticated, useSignedInUser } from './auth';

import { rootSiteSelector, siteFromRootSiteSelector } from 'selectors/root-site';
import { userState } from 'selectors/state-accessors';
import userActions from 'actions/user-actions';
import AxiosClient from 'configurations/api-client';

export const expertTypeSelector = createSelector(userState, (user) => get(user, 'isExpert', false));

export const useGetCurrentUserQuery = () => {
  const { siteSlug } = useParams();
  const dispatch = useDispatch();

  const { siteId } = useSelector(siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector);
  const isAuthenticated = useAuthenticated();

  const query = useQuery({
    queryKey: [siteId, 'current-user'],
    queryFn: () => dispatch(userActions.getCurrentUser(siteId)),
    enabled: !!isAuthenticated && !!siteId,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1_000,
  });

  return query;
};

export const useUserResearchBlockConfig = () => {
  const { id: userId } = useSignedInUser();

  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['research-block-config', userId], [userId]);

  const query = useQuery({
    queryKey,
    queryFn: () => AxiosClient.get('/users/current/research_block_preferences').then((response) => response.data),
    enabled: !!userId,
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { mutate: updateConfig } = useMutation({
    mutationFn: (payload) => !!userId && AxiosClient.patch('/users/current/update_selected_research_block', payload),
    onSuccess: (response) => queryClient.setQueryData(queryKey, () => get(response, 'data', {})),
  });

  const updateResearchBlockConfig = useCallback(
    (researchBlockId, childKey) =>
      updateConfig({
        researchBlockId,
        childKey,
      }),
    [updateConfig],
  );

  return useMemo(
    () => ({
      data: query.data,
      isFetching: query.isFetching,
      updateResearchBlockConfig,
    }),
    [query.data, query.isFetching, updateResearchBlockConfig],
  );
};
